import React, { useState, useMemo, useEffect } from 'react';
import {
  XMarkIcon,
  ScaleIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  CubeIcon,
  TruckIcon,
  LightBulbIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  ChevronRightIcon,
  BookOpenIcon,
  PuzzlePieceIcon,
  Squares2X2Icon,
  CpuChipIcon,
  ArchiveBoxIcon,
  ListBulletIcon,
  HashtagIcon,
  FolderIcon,
  BookmarkIcon,
  ShieldCheckIcon,
  LanguageIcon,
  GlobeAmericasIcon,
  BuildingOffice2Icon,
  NoSymbolIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

export interface ModalReglasLigieProps {
  abierto: boolean;
  onCerrar: () => void;
  moduloOrigen?: 'almacen' | 'despacho' | 'inteligencia' | 'general';
}

interface IncisoData {
  codigo: string;
  titulo: string;
  descripcion: string;
  ejemplo: string;
}

interface EstiloColor {
  badge: string;
  tag: string;
  bordeCard: string;
  accentBar: string;
  pillActive: string;
  lineaInciso: string;
  bgEjemplo: string;
  borderEjemplo: string;
  textEjemplo: string;
}

interface ReglaGeneralData {
  id: string;
  numero: string;
  badge: string;
  titulo: string;
  claveMnemotecnica: string;
  resumenSimple: string;
  incisos?: IncisoData[];
  ejemplo: string;
  aplicacionWMS: string;
  textoOficial: string;
  estilo: EstiloColor;
  icono: React.ComponentType<{ className?: string }>;
  gradienteIOS: string;
  shadowIOS: string;
}

interface ReglaComplementariaData {
  id: string;
  numero: string;
  titulo: string;
  categoria: string;
  claveMnemotecnica: string;
  resumenSimple: string;
  aplicacion: string;
  textoOficial: string;
  colorBadge: string;
  colorTag: string;
  icono: React.ComponentType<{ className?: string }>;
  gradienteIOS: string;
  shadowIOS: string;
}

// Componente Squircle estilo iOS / SF Symbols
const IconoIOS: React.FC<{
  icono: React.ComponentType<{ className?: string }>;
  gradiente: string;
  shadow?: string;
  tamano?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ icono: Icono, gradiente, shadow = '', tamano = 'md', className = '' }) => {
  const dimension = tamano === 'sm'
    ? 'w-7 h-7 sm:w-8 sm:h-8 rounded-[10px]'
    : tamano === 'lg'
      ? 'w-12 h-12 sm:w-14 sm:h-14 rounded-[18px]'
      : 'w-9 h-9 sm:w-10 sm:h-10 rounded-[13px]';

  const iconoTamano = tamano === 'sm' ? 'w-4 h-4' : tamano === 'lg' ? 'w-6 h-6 sm:w-7 sm:h-7' : 'w-5 h-5';

  return (
    <div
      className={`${dimension} ${gradiente} ${shadow} flex items-center justify-center shrink-0 shadow-md ring-1 ring-white/25 dark:ring-white/10 transition-transform ${className}`}
    >
      <Icono className={`${iconoTamano} text-white drop-shadow-xs`} />
    </div>
  );
};

const REGLAS_GENERALES: ReglaGeneralData[] = [
  {
    id: 'r1',
    numero: 'Regla 1',
    badge: '01',
    titulo: 'Prioridad del Texto de Partida y Notas Legales',
    claveMnemotecnica: 'El texto de partida manda siempre',
    resumenSimple:
      'Los títulos de las Secciones o Capítulos solo tienen un valor indicativo para ubicar temas. Legalmente, la clasificación está determinada 100% por los textos de las partidas y por las Notas de Sección o de Capítulo.',
    ejemplo:
      'Un caballo vivo de carreras se clasifica en 01.01 porque su texto dice textualmente "Caballos vivos", no por el título general del Capítulo 01. Si fuera un juguete, las notas legales del capítulo lo excluyen expresamente y lo envían al Capítulo 95.',
    aplicacionWMS:
      'En recepción de almacén y registro de SKUs, nunca asignes una fracción guiándote por el título genérico. Revisa siempre las notas legales excluyentes.',
    textoOficial:
      'Los títulos de las Secciones, de los Capítulos o de los Subcapítulos sólo tienen un valor indicativo, ya que la clasificación está determinada legalmente por los textos de las partidas y de las Notas de Sección o de Capítulo y, si no son contrarias a los textos de dichas partidas y Notas, de acuerdo con las Reglas siguientes.',
    icono: BookOpenIcon,
    gradienteIOS: 'bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-700',
    shadowIOS: 'shadow-indigo-500/30',
    estilo: {
      badge: 'bg-indigo-600 text-white',
      tag: 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/40',
      bordeCard: 'border-indigo-500/20 hover:border-indigo-500/40',
      accentBar: 'bg-indigo-600 dark:bg-indigo-400',
      pillActive: 'bg-indigo-600 text-white shadow-xs',
      lineaInciso: 'border-indigo-500/50',
      bgEjemplo: 'bg-indigo-50/60 dark:bg-indigo-950/20',
      borderEjemplo: 'border-indigo-200/60 dark:border-indigo-800/30',
      textEjemplo: 'text-indigo-700 dark:text-indigo-300',
    }
  },
  {
    id: 'r2',
    numero: 'Regla 2',
    badge: '02',
    titulo: 'Artículos Incompletos, Desmontados (2a) y Mezclas (2b)',
    claveMnemotecnica: 'Kits desarmados y materias mezcladas',
    resumenSimple:
      'Aplica cuando a un producto le faltan piezas secundarias, viene desarmado en piezas para armar en destino (IKEA/CKD), o cuando una materia prima viene combinada.',
    incisos: [
      {
        codigo: '2 a)',
        titulo: 'Incompletos o Desarmados (CKD / Kits)',
        descripcion:
          'Si al artículo le faltan piezas secundarias pero ya tiene la forma y función esencial del terminado, o viene en piezas desmontadas para armar, clasifica como el bien terminado.',
        ejemplo: 'Una bicicleta en caja sin pedales ni sillín, o un mueble modular desmontado de IKEA, clasifica como bicicleta (87.12) o mueble (94.03) terminado.'
      },
      {
        codigo: '2 b)',
        titulo: 'Materias Mezcladas o Asociadas',
        descripcion:
          'Las menciones a una materia alcanzan a dicha materia pura o asociada con otras sustancias. Si la mezcla genera duda con dos partidas, se remite a la Regla 3.',
        ejemplo: 'Café tostado adicionado con un 2% de azúcar para brillo conserva la clasificación de café.'
      }
    ],
    ejemplo:
      'Una línea de ensamblaje importada en 4 pallets desmontados con pernos y motores clasifica como la maquinaria completa terminada y no como chatarra o partes dispersas.',
    aplicacionWMS:
      'Al ingresar mercancía CKD distribuida en varios huecos de rack, todos los bultos deben ampararse bajo el SKU maestro del producto terminado.',
    textoOficial:
      '2. a) Cualquier referencia a un artículo en una partida determinada alcanza al artículo incluso incompleto o sin terminar, siempre que éste presente las características esenciales del artículo completo o terminado. Alcanza también al artículo completo o terminado cuando se presente desmontado o sin montar todavía.\n\nb) Cualquier referencia a una materia en una partida determinada alcanza a dicha materia, incluso mezclada o asociada con otras materias. La clasificación de productos mezclados o compuestos se efectuará de acuerdo con la Regla 3.',
    icono: PuzzlePieceIcon,
    gradienteIOS: 'bg-gradient-to-br from-sky-400 via-sky-500 to-blue-600',
    shadowIOS: 'shadow-sky-500/30',
    estilo: {
      badge: 'bg-sky-600 text-white',
      tag: 'bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800/40',
      bordeCard: 'border-sky-500/20 hover:border-sky-500/40',
      accentBar: 'bg-sky-600 dark:bg-sky-400',
      pillActive: 'bg-sky-600 text-white shadow-xs',
      lineaInciso: 'border-sky-500/50',
      bgEjemplo: 'bg-sky-50/60 dark:bg-sky-950/20',
      borderEjemplo: 'border-sky-200/60 dark:border-sky-800/30',
      textEjemplo: 'text-sky-700 dark:text-sky-300',
    }
  },
  {
    id: 'r3',
    numero: 'Regla 3',
    badge: '03',
    titulo: 'Mercancías con Dos o Más Partidas Posibles',
    claveMnemotecnica: '1° Específica ➔ 2° Esencia ➔ 3° Última',
    resumenSimple:
      'Cuando un producto parece encajar en dos o más partidas, se resuelve aplicando este orden en 3 pasos sucesivos e inapelables:',
    incisos: [
      {
        codigo: '3 a)',
        titulo: 'Partida Más Específica',
        descripcion:
          'La partida que describe el artículo con mayor precisión técnica derrota a cualquier partida genérica o residual.',
        ejemplo: 'Neumáticos de caucho van en 40.11 (Neumáticos) y no en 87.08 (Partes de vehículos).'
      },
      {
        codigo: '3 b)',
        titulo: 'Carácter Esencial (Sets y Combos)',
        descripcion:
          'En surtidos para venta minorista o manufacturas compuestas, la mercancía se clasifica según el componente que confiere su utilidad principal o mayor valor.',
        ejemplo: 'Un kit de peluquería (máquina rasuradora eléctrica, tijera y peine en estuche) clasifica por la máquina eléctrica (85.10).'
      },
      {
        codigo: '3 c)',
        titulo: 'Última Partida Numérica',
        descripcion:
          'Si las reglas 3a y 3b no lo definen (empate técnico 50/50), se asigna la partida con el código numérico más alto.',
        ejemplo: 'Prenda compuesta 50% de lana (Capítulo 51) y 50% de seda (Capítulo 50) clasifica en Lana (51) por ser número posterior.'
      }
    ],
    ejemplo:
      'Un set de regalo navideño con una botella de tequila, dos vasos tequileros y una charola de plástico acondicionado para venta al público clasifica íntegramente como Tequila bajo la Regla 3b.',
    aplicacionWMS:
      'Regla indispensable para dar de alta SKUs Combo o Packs Promocionales: se registran con una sola fracción arancelaria correspondiente al elemento esencial.',
    textoOficial:
      'Cuando una mercancía pudiera clasificarse, en principio, en dos o más partidas:\n\na) La partida con descripción más específica tendrá prioridad sobre las partidas de alcance más genérico.\n\nb) Los productos mezclados, manufacturas compuestas y mercancías en juegos o surtidos para venta al por menor, se clasificarán según la materia o artículo que les confiera su carácter esencial.\n\nc) Cuando las Reglas 3 a) y 3 b) no permitan efectuar la clasificación, la mercancía se clasificará en la última partida por orden de numeración.',
    icono: Squares2X2Icon,
    gradienteIOS: 'bg-gradient-to-br from-amber-400 via-orange-500 to-amber-600',
    shadowIOS: 'shadow-amber-500/30',
    estilo: {
      badge: 'bg-amber-600 text-white',
      tag: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/40',
      bordeCard: 'border-amber-500/20 hover:border-amber-500/40',
      accentBar: 'bg-amber-600 dark:bg-amber-400',
      pillActive: 'bg-amber-600 text-white shadow-xs',
      lineaInciso: 'border-amber-500/50',
      bgEjemplo: 'bg-amber-50/60 dark:bg-amber-950/20',
      borderEjemplo: 'border-amber-200/60 dark:border-amber-800/30',
      textEjemplo: 'text-amber-700 dark:text-amber-300',
    }
  },
  {
    id: 'r4',
    numero: 'Regla 4',
    badge: '04',
    titulo: 'Clasificación por Mayor Analogía (Último Recurso)',
    claveMnemotecnica: 'Mayor analogía para tecnologías inéditas',
    resumenSimple:
      'Mercancías que no puedan clasificarse con las reglas anteriores se clasificarán en la partida del producto al que más se parezcan en función o materia. Aplica excepcionalmente ante inventos o innovaciones sin partida previa.',
    ejemplo:
      'Cuando surgieron los primeros drones comerciales o dispositivos de realidad virtual holográfica, al no existir subpartida específica, se clasificaron temporalmente por analogía con aeronaves o cámaras de video.',
    aplicacionWMS:
      'Si se adquiere un prototipo industrial o hardware experimental sin fracción en catálogo, requiere consulta previa formal fundada en analogía.',
    textoOficial:
      'Las mercancías que no puedan clasificarse aplicando las Reglas anteriores, se clasificarán en la partida que comprenda aquellas con las que tengan mayor analogía.',
    icono: CpuChipIcon,
    gradienteIOS: 'bg-gradient-to-br from-purple-500 via-fuchsia-600 to-purple-700',
    shadowIOS: 'shadow-purple-500/30',
    estilo: {
      badge: 'bg-purple-600 text-white',
      tag: 'bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800/40',
      bordeCard: 'border-purple-500/20 hover:border-purple-500/40',
      accentBar: 'bg-purple-600 dark:bg-purple-400',
      pillActive: 'bg-purple-600 text-white shadow-xs',
      lineaInciso: 'border-purple-500/50',
      bgEjemplo: 'bg-purple-50/60 dark:bg-purple-950/20',
      borderEjemplo: 'border-purple-200/60 dark:border-purple-800/30',
      textEjemplo: 'text-purple-700 dark:text-purple-300',
    }
  },
  {
    id: 'r5',
    numero: 'Regla 5',
    badge: '05',
    titulo: 'Estuches Especiales (5a) y Envases / Embalajes (5b)',
    claveMnemotecnica: 'Estuche con el bien / Tara retornable aparte',
    resumenSimple:
      'Regula el tratamiento de estuches específicos de uso prolongado y la distinción entre envases ordinarios y envases retornables de uso repetitivo.',
    incisos: [
      {
        codigo: '5 a)',
        titulo: 'Estuches de Uso Prolongado',
        descripcion:
          'Estuches moldeados a la silueta de un bien (para guitarra, cámara, saxofón, microscopio) se clasifican con el artículo si se presentan juntos.',
        ejemplo: 'El estuche rígido termoformado de un saxofón profesional clasifica con el saxofón (92.05) y no como marroquinería.'
      },
      {
        codigo: '5 b)',
        titulo: 'Envases Habituales vs de Uso Repetido (Tara)',
        descripcion:
          'Las cajas de cartón o blisters comunes clasifican con el producto. Pero los envases reutilizables (tanques presurizados, cilindros de gas, tarimas) se clasifican por separado.',
        ejemplo: 'Cilindros de acero presurizado para gas o racks metálicos automotrices retornables.'
      }
    ],
    ejemplo:
      'En almacén y cubicaje, las tarimas de madera retornables (pallets) y racks metálicos son tara logística, no se suman al costo unitario del producto.',
    aplicacionWMS:
      'Regla de oro para cubicaje en WMS: la tara de tarimas de madera o racks retornables debe aislarse del valor de inventario.',
    textoOficial:
      'a) Los estuches para cámaras, instrumentos musicales, armas, collares y continentes similares, especialmente apropiados para contener un artículo determinado, de uso prolongado y presentados con dichos artículos, se clasificarán con ellos.\n\nb) Salvo lo dispuesto en 5 a), los envases que contengan mercancías se clasificarán con ellas cuando sean de los tipos normalmente utilizados. Esta disposición no es obligatoria cuando los envases sean susceptibles de ser utilizados de manera repetida.',
    icono: ArchiveBoxIcon,
    gradienteIOS: 'bg-gradient-to-br from-emerald-400 via-emerald-500 to-teal-700',
    shadowIOS: 'shadow-emerald-500/30',
    estilo: {
      badge: 'bg-emerald-600 text-white',
      tag: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/40',
      bordeCard: 'border-emerald-500/20 hover:border-emerald-500/40',
      accentBar: 'bg-emerald-600 dark:bg-emerald-400',
      pillActive: 'bg-emerald-600 text-white shadow-xs',
      lineaInciso: 'border-emerald-500/50',
      bgEjemplo: 'bg-emerald-50/60 dark:bg-emerald-950/20',
      borderEjemplo: 'border-emerald-200/60 dark:border-emerald-800/30',
      textEjemplo: 'text-emerald-700 dark:text-emerald-300',
    }
  },
  {
    id: 'r6',
    numero: 'Regla 6',
    badge: '06',
    titulo: 'Clasificación en Subpartidas del Mismo Nivel',
    claveMnemotecnica: 'Mismo nivel: 1 guion (-) primero, 2 (--) después',
    resumenSimple:
      'Las subpartidas (código a 6 dígitos) se determinan comparando únicamente subpartidas del mismo nivel: primero se comparan las de 1 guion (-), y solo después de elegir esa rama, se comparan las de 2 guiones (--).',
    ejemplo:
      'Al clasificar carne bovina en 02.01, primero eliges entre "En canales" (-) o "Demás cortes" (-). Dentro de "Demás cortes", defines si es "Con hueso" (--) o "Deshuesada" (--).',
    aplicacionWMS:
      'La estructura de árbol arancelario en LogixPort refleja esta regla: no puedes saltar a un NICO sin haber seleccionado la subpartida del nivel correspondiente.',
    textoOficial:
      'La clasificación de mercancías en las subpartidas de una misma partida está determinada legalmente por los textos de estas subpartidas y de las Notas de subpartida así como, mutatis mutandis, por las Reglas anteriores, bien entendido que sólo pueden compararse subpartidas del mismo nivel. A efectos de esta Regla, también se aplican las Notas de Sección y de Capítulo.',
    icono: ListBulletIcon,
    gradienteIOS: 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700',
    shadowIOS: 'shadow-blue-500/30',
    estilo: {
      badge: 'bg-blue-600 text-white',
      tag: 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40',
      bordeCard: 'border-blue-500/20 hover:border-blue-500/40',
      accentBar: 'bg-blue-600 dark:bg-blue-400',
      pillActive: 'bg-blue-600 text-white shadow-xs',
      lineaInciso: 'border-blue-500/50',
      bgEjemplo: 'bg-blue-50/60 dark:bg-blue-950/20',
      borderEjemplo: 'border-blue-200/60 dark:border-blue-800/30',
      textEjemplo: 'text-blue-700 dark:text-blue-300',
    }
  }
];

const REGLAS_COMPLEMENTARIAS: ReglaComplementariaData[] = [
  {
    id: 'rc1',
    numero: '1a',
    titulo: 'Validez Uniforme en Fracciones de 8 Dígitos',
    categoria: 'Estructura & 8 Dígitos',
    claveMnemotecnica: 'Misma fuerza legal en 8 dígitos',
    resumenSimple:
      'Las Reglas Generales aplican con la misma fuerza legal para definir la fracción arancelaria específica (8 dígitos) y el NICO en México.',
    aplicacion: 'Garantiza coherencia legal en dictámenes arancelarios, pedimentos y auditorías de comercio exterior.',
    textoOficial:
      'Las Reglas Generales para la interpretación de la Tarifa de la Ley de los Impuestos Generales de Importación y de Exportación son igualmente válidas para establecer dentro de cada subpartida la fracción arancelaria aplicable, excepto para la Sección XXII.',
    icono: HashtagIcon,
    gradienteIOS: 'bg-gradient-to-br from-indigo-500 via-indigo-600 to-blue-600',
    shadowIOS: 'shadow-indigo-500/25',
    colorBadge: 'bg-indigo-600 text-white',
    colorTag: 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/40'
  },
  {
    id: 'rc2',
    numero: '2a',
    titulo: 'Estructura de 22 Secciones y Código 99 (Los Demás)',
    categoria: 'Estructura & 8 Dígitos',
    claveMnemotecnica: 'Código 99 = Cajón de "Los demás"',
    resumenSimple:
      'Establece el mapa oficial de la LIGIE: 22 Secciones y código de 8 dígitos. Si un producto no cabe en las fracciones 01 a 98, cae obligatoriamente en la fracción genérica 99 ("Los demás").',
    aplicacion: 'Estructura la base de datos de aranceles y NICO en toda la plataforma LogixPort.',
    textoOficial:
      'La Tarifa del artículo 1 de esta Ley está dividida en 22 Secciones... Las fracciones arancelarias estarán formadas por un código de 8 dígitos (Capítulo: 2, Partida: 4, Subpartida: 6, Fracción: 8), reservando el 99 para las mercancías que no estén en 01 a 98.',
    icono: FolderIcon,
    gradienteIOS: 'bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800',
    shadowIOS: 'shadow-indigo-500/25',
    colorBadge: 'bg-indigo-600 text-white',
    colorTag: 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/40'
  },
  {
    id: 'rc3',
    numero: '3a',
    titulo: 'Notas Explicativas del DOF Obligatorias',
    categoria: 'Criterios Oficiales',
    claveMnemotecnica: 'Notas DOF son de aplicación obligatoria',
    resumenSimple:
      'Las Notas Explicativas publicadas conjuntamente por la Secretaría de Economía y la SHCP en el DOF tienen valor de ley obligatorio para sustentar clasificaciones.',
    aplicacion: 'Cita obligatoria en cualquier dictamen técnico ante el SAT o la ANAM.',
    textoOficial:
      'Para los efectos de interpretación y aplicación de la Tarifa, la Secretaría de Economía, conjuntamente con la de Hacienda y Crédito Público, dará a conocer mediante Acuerdos en el DOF las Notas Explicativas, cuya aplicación es obligatoria.',
    icono: BookmarkIcon,
    gradienteIOS: 'bg-gradient-to-br from-purple-500 via-purple-600 to-pink-600',
    shadowIOS: 'shadow-purple-500/25',
    colorBadge: 'bg-purple-600 text-white',
    colorTag: 'bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800/40'
  },
  {
    id: 'rc4',
    numero: '4a',
    titulo: 'Criterios de Clasificación Vinculantes',
    categoria: 'Criterios Oficiales',
    claveMnemotecnica: 'Criterios SHCP unifican a todas las aduanas',
    resumenSimple:
      'La SHCP publica Criterios Arancelarios en el DOF para que todas las aduanas del país apliquen el mismo criterio ante productos controvertidos.',
    aplicacion: 'Resuelve discrepancias formales entre agentes aduanales y autoridades aduaneras.',
    textoOficial:
      'Con el objeto de mantener la unidad de criterio en la clasificación de las mercancías, la SHCP expedirá mediante publicación en el DOF los Criterios de Clasificación Arancelaria, de carácter obligatorio.',
    icono: ShieldCheckIcon,
    gradienteIOS: 'bg-gradient-to-br from-violet-500 via-purple-600 to-purple-800',
    shadowIOS: 'shadow-purple-500/25',
    colorBadge: 'bg-purple-600 text-white',
    colorTag: 'bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800/40'
  },
  {
    id: 'rc5',
    numero: '5a',
    titulo: 'Catálogo Oficial de Abreviaturas',
    categoria: 'Medidas & Pesos',
    claveMnemotecnica: 'Abreviaturas oficiales (kg, pza, OMA)',
    resumenSimple:
      'Define el listado oficial de abreviaturas para unidades de medida (kg, pza, m3), países y organismos internacionales (OMA, OMC).',
    aplicacion: 'Estandariza unidades en facturas, listas de empaque, WMS y pedimentos.',
    textoOficial:
      'Las abreviaturas empleadas en la Tarifa son: De cantidad (kg, t, m3, pza, l), de países y de organismos internacionales (OMA, OMC, SA).',
    icono: LanguageIcon,
    gradienteIOS: 'bg-gradient-to-br from-cyan-400 via-teal-500 to-teal-700',
    shadowIOS: 'shadow-cyan-500/25',
    colorBadge: 'bg-cyan-600 text-white',
    colorTag: 'bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800/40'
  },
  {
    id: 'rc6',
    numero: '6a',
    titulo: 'Límites de Peso: Siempre PESO NETO',
    categoria: 'Medidas & Pesos',
    claveMnemotecnica: 'Límites en tarifa = Siempre peso neto (sin tara)',
    resumenSimple:
      'Cualquier límite de peso en la tarifa arancelaria se refiere exclusivamente al peso neto de la mercancía, sin incluir tarimas ni embalajes.',
    aplicacion: 'Clave en báscula portuaria (VGM) para descontar la tara y no clasificar erróneamente.',
    textoOficial:
      'Cuando se mencionen límites de peso en la presente Tarifa, se referirán exclusivamente al peso de las mercancías, salvo disposición expresa en contrario.',
    icono: ScaleIcon,
    gradienteIOS: 'bg-gradient-to-br from-teal-500 via-emerald-600 to-emerald-700',
    shadowIOS: 'shadow-teal-500/25',
    colorBadge: 'bg-cyan-600 text-white',
    colorTag: 'bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800/40'
  },
  {
    id: 'rc7',
    numero: '7a',
    titulo: 'Preferencias por Tratados (T-MEC y TLCs)',
    categoria: 'Tratados & Fomento',
    claveMnemotecnica: '0% IGI con Certificado de Origen válido',
    resumenSimple:
      'Permite importar con arancel preferencial (0% IGI) si se cuenta con un certificado de origen válido emitido bajo tratados internacionales.',
    aplicacion: 'Sustento legal para registrar aranceles preferenciales en pedimento.',
    textoOficial:
      'Para dar cumplimiento a las negociaciones de tratados internacionales, se incluirán en las fracciones o en Apéndices indicando la fracción, el tratamiento preferencial y el país otorgado.',
    icono: GlobeAmericasIcon,
    gradienteIOS: 'bg-gradient-to-br from-amber-400 via-orange-500 to-amber-600',
    shadowIOS: 'shadow-amber-500/25',
    colorBadge: 'bg-amber-600 text-white',
    colorTag: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/40'
  },
  {
    id: 'rc8',
    numero: '8a',
    titulo: 'Regla 8va: Importación en Remesas Parciales',
    categoria: 'Tratados & Fomento',
    claveMnemotecnica: 'Plantas completas en varios barcos o aduanas',
    resumenSimple:
      'Permite a empresas con registro de fabricante importar por una o varias aduanas plantas industriales completas o insumos divididos en múltiples contenedores.',
    aplicacion: 'Indispensable en puertos marítimos como Manzanillo para desembarque de maquinaria en varios buques.',
    textoOficial:
      'Previa autorización de la Secretaría de Economía: a) Se consideran como artículos completos los que se importen en varias remesas o aduanas por empresas con registro de fabricante... b) Podrán importarse artículos desmontados para ampliar planta.',
    icono: BuildingOffice2Icon,
    gradienteIOS: 'bg-gradient-to-br from-orange-500 via-amber-600 to-red-600',
    shadowIOS: 'shadow-orange-500/25',
    colorBadge: 'bg-amber-600 text-white',
    colorTag: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/40'
  },
  {
    id: 'rc9',
    numero: '9a',
    titulo: 'Mercancías No Gravadas (Exentas de Impuesto)',
    categoria: 'Plazos & Exenciones',
    claveMnemotecnica: 'Exentos: urnas con restos, cartas y muestras',
    resumenSimple:
      'No pagan impuestos: ataúdes o urnas con restos humanos, cartas de correspondencia postal ordinaria y muestras de laboratorio sin valor comercial.',
    aplicacion: 'Regula el cruce de muestras técnicas para pruebas de calidad sin pago de arancel.',
    textoOficial:
      'No se considerarán como mercancías y no se gravarán: ataúdes y urnas con cadáveres o restos; piezas postales obliteradas; y muestras sin valor comercial.',
    icono: NoSymbolIcon,
    gradienteIOS: 'bg-gradient-to-br from-emerald-400 via-green-500 to-emerald-700',
    shadowIOS: 'shadow-emerald-500/25',
    colorBadge: 'bg-emerald-600 text-white',
    colorTag: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/40'
  },
  {
    id: 'rc10',
    numero: '10a',
    titulo: 'Facultades de Autoridad y Plazo de Controversia',
    categoria: 'Plazos & Exenciones',
    claveMnemotecnica: '15 días naturales para defender clasificación',
    resumenSimple:
      'Ante una duda o controversia arancelaria en aduana, la autoridad otorga un plazo de 15 días naturales (prorrogable por otros 15) para entregar pruebas técnicas.',
    aplicacion: 'Plazo legal clave para el importador ante incidencias en semáforo fiscal.',
    textoOficial:
      'Las autoridades aduaneras podrán exigir en caso de duda los elementos que permitan la identificación arancelaria; que los interesados deberán proporcionar en un plazo de 15 días naturales, prorrogable por un término igual.',
    icono: ClockIcon,
    gradienteIOS: 'bg-gradient-to-br from-rose-500 via-rose-600 to-pink-600',
    shadowIOS: 'shadow-rose-500/25',
    colorBadge: 'bg-rose-600 text-white',
    colorTag: 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/40'
  }
];

export const ModalReglasLigie: React.FC<ModalReglasLigieProps> = ({
  abierto,
  onCerrar,
  moduloOrigen = 'general'
}) => {
  const [tabActiva, setTabActiva] = useState<'generales' | 'complementarias' | 'practica'>('generales');
  const [reglaGeneralId, setReglaGeneralId] = useState<string>('r1');
  const [reglaComplementariaId, setReglaComplementariaId] = useState<string>('rc1');
  const [busqueda, setBusqueda] = useState('');
  const [copiadoId, setCopiadoId] = useState<string | null>(null);

  // Manejo de teclado (Cerrar con Escape) y bloqueo de scroll en el body
  useEffect(() => {
    if (!abierto) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCerrar();
      }
    };

    const scrollOriginal = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = scrollOriginal;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [abierto, onCerrar]);

  const copiarTexto = (id: string, texto: string) => {
    navigator.clipboard.writeText(texto);
    setCopiadoId(id);
    setTimeout(() => setCopiadoId(null), 2000);
  };

  const reglasGeneralesFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return REGLAS_GENERALES;
    return REGLAS_GENERALES.filter(
      r =>
        r.numero.toLowerCase().includes(q) ||
        r.titulo.toLowerCase().includes(q) ||
        r.claveMnemotecnica.toLowerCase().includes(q) ||
        r.resumenSimple.toLowerCase().includes(q) ||
        r.ejemplo.toLowerCase().includes(q)
    );
  }, [busqueda]);

  const reglasComplementariasFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return REGLAS_COMPLEMENTARIAS;
    return REGLAS_COMPLEMENTARIAS.filter(
      r =>
        r.numero.toLowerCase().includes(q) ||
        r.titulo.toLowerCase().includes(q) ||
        r.categoria.toLowerCase().includes(q) ||
        r.claveMnemotecnica.toLowerCase().includes(q) ||
        r.resumenSimple.toLowerCase().includes(q) ||
        r.aplicacion.toLowerCase().includes(q)
    );
  }, [busqueda]);

  // Regla actualmente activa para el panel inspector derecho
  const reglaGeneralSeleccionada = useMemo(() => {
    return REGLAS_GENERALES.find(r => r.id === reglaGeneralId) || REGLAS_GENERALES[0];
  }, [reglaGeneralId]);

  const reglaComplementariaSeleccionada = useMemo(() => {
    return REGLAS_COMPLEMENTARIAS.find(r => r.id === reglaComplementariaId) || REGLAS_COMPLEMENTARIAS[0];
  }, [reglaComplementariaId]);

  if (!abierto) return null;

  return (
      <div 
      className="fixed inset-0 z-[120] flex items-center justify-center p-2 sm:p-4 lg:p-6 bg-slate-900/75 dark:bg-black/85 backdrop-blur-md overflow-y-auto overflow-x-hidden animate-fadeIn"
      onClick={onCerrar}
      role="dialog"
      aria-modal="true"
    >
        <div 
        className="relative bg-white dark:bg-[#0f0f10] rounded-2xl sm:rounded-[2rem] shadow-2xl shadow-black/50 w-full max-w-6xl my-auto max-h-[92vh] flex flex-col border border-slate-200/70 dark:border-white/10 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* HEADER DEL MODAL (Estilo LogixPort WMS / Control Room) */}
        <div className="sticky top-0 z-20 bg-white/95 dark:bg-[#0f0f10]/95 backdrop-blur-xl border-b border-slate-100 dark:border-white/5 px-4 sm:px-8 py-3.5 sm:py-4 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-[13px] bg-gradient-to-br from-blue-500 via-indigo-600 to-indigo-800 flex items-center justify-center text-white shadow-md shadow-blue-500/25 ring-1 ring-white/20 shrink-0">
                <ScaleIcon className="h-5 w-5 drop-shadow-xs" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-extrabold uppercase tracking-widest text-blue-600 dark:text-blue-400">
                    Artículo 2 · LIGIE
                  </span>
                  <span className="text-[10px] text-slate-300 dark:text-zinc-600">•</span>
                  <span className="text-[10px] font-semibold text-slate-500 dark:text-zinc-400">
                    {moduloOrigen === 'almacen' ? 'Módulo Almacén Práctico' : moduloOrigen === 'despacho' ? 'Módulo Despacho Portuario' : 'Inteligencia Aduanera'}
                  </span>
                </div>
                <h3 className="text-base sm:text-xl font-extrabold bg-gradient-to-r from-slate-900 to-slate-600 bg-clip-text text-transparent dark:from-white dark:to-zinc-400 tracking-tight leading-tight">
                  Reglas Generales y Complementarias
                </h3>
              </div>
            </div>

              <button 
            type="button"
              onClick={onCerrar}
              className="p-2 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-500 dark:text-zinc-400 transition-colors cursor-pointer"
              title="Cerrar ventana (Escape)"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* BARRA DE PESTAÑAS (Estilo Clean de Navegación) */}
          <div className="px-4 sm:px-8 py-2.5 border-b border-slate-200/80 dark:border-white/5 bg-slate-50/50 dark:bg-black/20 flex flex-wrap items-center justify-between gap-3 shrink-0">
            <div className="flex gap-1.5 p-1 bg-slate-200/60 dark:bg-white/[0.04] rounded-xl border border-slate-200/80 dark:border-white/5 overflow-x-auto no-scrollbar">
              <button
                type="button"
                onClick={() => setTabActiva('generales')}
              className={`flex items-center gap-2 px-3.5 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${tabActiva === 'generales'
                    ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-xs'
                    : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <span>6 Reglas Generales</span>
              <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-300 font-extrabold">
                OMA
              </span>
            </button>
            <button
              type="button"
              onClick={() => setTabActiva('complementarias')}
              className={`flex items-center gap-2 px-3.5 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${tabActiva === 'complementarias'
                  ? 'bg-white dark:bg-white/10 text-amber-600 dark:text-amber-300 shadow-xs'
                  : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
            <span>10 Reglas Complementarias</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-300 font-extrabold">
              México
            </span>
          </button>
          <button
            type="button"
            onClick={() => setTabActiva('practica')}
              className={`flex items-center gap-2 px-3.5 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${tabActiva === 'practica'
                ? 'bg-white dark:bg-white/10 text-emerald-600 dark:text-emerald-400 shadow-xs'
                : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
          <SparklesIcon className="w-3.5 h-3.5 text-emerald-500" />
          <span>Matriz de Aplicación WMS / Puerto</span>
        </button>
      </div>

      <div className="text-xs text-slate-500 dark:text-zinc-400 hidden sm:block">
        {tabActiva === 'generales' && 'Normas rectoras del Sistema Armonizado'}
        {tabActiva === 'complementarias' && 'Normativa nacional obligatoria en México'}
        {tabActiva === 'practica' && 'Guía operativa de racks, pesaje y pedimentos'}
      </div>
    </div>

        {/* CUERPO PRINCIPAL */ }
  <div className="flex-1 overflow-hidden flex flex-col">
    {/* VISTA 1 Y 2: MASTER-DETAIL CON ICONOS ESTILO IOS */}
    {(tabActiva === 'generales' || tabActiva === 'complementarias') && (
      <div className="grid grid-cols-1 lg:grid-cols-12 flex-1 overflow-hidden">
        {/* PANEL IZQUIERDO: SELECTOR CON ICONOS IOS (COL-SPAN-4) */}
        <div className="lg:col-span-4 xl:col-span-4 border-b lg:border-b-0 lg:border-r border-slate-200/80 dark:border-white/5 flex flex-col bg-slate-50/50 dark:bg-black/20 overflow-hidden">
          {/* Buscador Rápido en Sidebar */}
          <div className="p-3 sm:p-4 border-b border-slate-200/80 dark:border-white/5 shrink-0">
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 dark:text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                placeholder="Filtrar por término, ejemplo o SKU..."
                className="w-full pl-9 pr-7 py-2 text-xs rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.04] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-500 outline-none transition-all focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              {busqueda && (
                <button
                  type="button"
                  onClick={() => setBusqueda('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white text-xs cursor-pointer"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Lista Vertical con Squircles Tipo iOS */}
          <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-1.5 scrollbar-thin">
            {tabActiva === 'generales' ? (
              reglasGeneralesFiltradas.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400">
                  No hay reglas con &quot;{busqueda}&quot;
                </div>
              ) : (
                reglasGeneralesFiltradas.map((r) => {
                  const isSelected = r.id === reglaGeneralSeleccionada.id;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setReglaGeneralId(r.id)}
                            className={`w-full text-left p-2.5 sm:p-3 rounded-2xl transition-all cursor-pointer flex items-center justify-between gap-3 ${isSelected
                          ? 'bg-white dark:bg-white/[0.08] shadow-md border border-slate-200 dark:border-white/15'
                          : 'hover:bg-white/60 dark:hover:bg-white/[0.03] border border-transparent'
                            }`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              {/* Icono Tipo iOS Squircle */}
                              <IconoIOS
                                icono={r.icono}
                                gradiente={r.gradienteIOS}
                                shadow={r.shadowIOS}
                                tamano="md"
                                className={isSelected ? 'scale-105' : ''}
                              />
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <h4 className={`text-xs font-black truncate ${isSelected ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-zinc-300'}`}>
                                    {r.numero}
                                  </h4>
                                </div>
                                <p className="text-[11px] text-slate-500 dark:text-zinc-400 truncate mt-0.5 font-medium">
                                  {r.claveMnemotecnica}
                                </p>
                              </div>
                            </div>
                            <ChevronRightIcon className={`w-4 h-4 shrink-0 transition-transform ${isSelected ? 'text-blue-500 translate-x-0.5' : 'text-slate-300 dark:text-zinc-600'}`} />
                          </button>
                        );
                      })
                    )
                  ) : (
                    reglasComplementariasFiltradas.length === 0 ? (
                      <div className="p-6 text-center text-xs text-slate-400">
                        No hay reglas con &quot;{busqueda}&quot;
                      </div>
                    ) : (
                      reglasComplementariasFiltradas.map((c) => {
                        const isSelected = c.id === reglaComplementariaSeleccionada.id;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => setReglaComplementariaId(c.id)}
                            className={`w-full text-left p-2.5 sm:p-3 rounded-2xl transition-all cursor-pointer flex items-center justify-between gap-3 ${isSelected
                      ? 'bg-white dark:bg-white/[0.08] shadow-md border border-slate-200 dark:border-white/15'
                      : 'hover:bg-white/60 dark:hover:bg-white/[0.03] border border-transparent'
                            }`}
                          >
            <div className="flex items-center gap-3 min-w-0">
              <IconoIOS
                icono={c.icono}
                gradiente={c.gradienteIOS}
                shadow={c.shadowIOS}
                tamano="md"
                className={isSelected ? 'scale-105' : ''}
              />
              <div className="min-w-0">
                  <h4 className={`text-xs font-black truncate ${isSelected ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-zinc-300'}`}>
                    Regla {c.numero}
                  </h4>
                    <p className="text-[11px] text-slate-500 dark:text-zinc-400 truncate mt-0.5 font-medium">
                      {c.claveMnemotecnica}
                    </p>
                  </div>
              </div>
              <ChevronRightIcon className={`w-4 h-4 shrink-0 transition-transform ${isSelected ? 'text-amber-500 translate-x-0.5' : 'text-slate-300 dark:text-zinc-600'}`} />
            </button>
            );
                      })
            )
                  )}
          </div>
        </div>

        {/* PANEL DERECHO: INSPECTOR & FICHA TÉCNICA (COL-SPAN-8) */}
        <div className="lg:col-span-8 xl:col-span-8 flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-5 bg-white dark:bg-[#0f0f10]">
          {tabActiva === 'generales' ? (
            // FICHA TÉCNICA DE REGLA GENERAL
            <div className="space-y-5">
              {/* Header de la Ficha Técnica con Squircle Hero iOS */}
              <div className="flex items-start justify-between gap-4 pb-4 border-b border-slate-200/80 dark:border-white/5">
                    <div className="flex items-center gap-3.5 min-w-0">
                      <IconoIOS
                        icono={reglaGeneralSeleccionada.icono}
                        gradiente={reglaGeneralSeleccionada.gradienteIOS}
                        shadow={reglaGeneralSeleccionada.shadowIOS}
                        tamano="lg"
                      />
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2.5 py-0.5 rounded-lg ${reglaGeneralSeleccionada.estilo.badge} font-black text-xs tracking-wider uppercase shadow-xs`}>
                            {reglaGeneralSeleccionada.numero}
                          </span>
                          <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${reglaGeneralSeleccionada.estilo.tag} flex items-center gap-1`}>
                            <LightBulbIcon className="w-3.5 h-3.5 shrink-0" />
                            <span>{reglaGeneralSeleccionada.claveMnemotecnica}</span>
                          </span>
                        </div>
                        <h2 className="text-base sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight leading-snug">
                          {reglaGeneralSeleccionada.titulo}
                        </h2>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => copiarTexto(reglaGeneralSeleccionada.id, `${reglaGeneralSeleccionada.numero} LIGIE: ${reglaGeneralSeleccionada.titulo}\n\nPrincipio:\n${reglaGeneralSeleccionada.resumenSimple}\n\nEjemplo:\n${reglaGeneralSeleccionada.ejemplo}\n\nTexto de Ley:\n${reglaGeneralSeleccionada.textoOficial}`)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-slate-700 dark:text-zinc-300 text-xs font-bold hover:bg-slate-100 dark:hover:bg-white/10 transition-colors shrink-0 cursor-pointer"
                      title="Copiar ficha de esta regla"
                    >
                      {copiadoId === reglaGeneralSeleccionada.id ? (
                        <>
                          <CheckIcon className="w-4 h-4 text-emerald-500" />
                          <span className="text-emerald-600 dark:text-emerald-400">¡Copiado!</span>
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentCheckIcon className="w-4 h-4" />
                          <span className="hidden sm:inline">Copiar Ficha</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Bento Card 1: Principio de Interpretación (En Sencillo) */}
                  <div className="p-4 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-white/5 bg-slate-50/60 dark:bg-white/[0.02] space-y-2">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400">
                      <div className="w-5 h-5 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                        <AcademicCapIcon className="w-3.5 h-3.5" />
                      </div>
                      <span>Criterio Operativo en Lenguaje Claro</span>
                    </div>
                    <p className="text-sm sm:text-base text-slate-800 dark:text-zinc-200 leading-relaxed font-medium">
                      {reglaGeneralSeleccionada.resumenSimple}
                    </p>
                  </div>

                  {/* Bento Card 2: Desglose de Incisos (Si los tiene) */}
                  {reglaGeneralSeleccionada.incisos && reglaGeneralSeleccionada.incisos.length > 0 && (
                    <div className="space-y-3">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                          <span>Desglose de Incisos Oficiales</span>
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {reglaGeneralSeleccionada.incisos.map((inc) => (
                            <div
                              key={inc.codigo}
                              className="p-4 rounded-2xl border border-slate-200/80 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-xs space-y-2"
                            >
                              <div className="flex items-center gap-2 font-bold text-xs text-slate-900 dark:text-white">
                                <span className="px-1.5 py-0.5 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 font-mono text-[11px]">
                                  {inc.codigo}
                                </span>
                                <span>{inc.titulo}</span>
                              </div>
                              <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                                {inc.descripcion}
                              </p>
                              <div className="text-[11px] p-2 rounded-xl bg-slate-100/70 dark:bg-black/30 text-slate-700 dark:text-zinc-300">
                                <strong className="text-slate-900 dark:text-white">Ejemplo:</strong> {inc.ejemplo}
                              </div>
                            </div>
                          ))}
                        </div>
                    </div>
                  )}

                  {/* Bento Card 3: Caso de Estudio Real & Aplicación Logística */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className={`p-4 sm:p-5 rounded-2xl sm:rounded-3xl border ${reglaGeneralSeleccionada.estilo.borderEjemplo} ${reglaGeneralSeleccionada.estilo.bgEjemplo} space-y-2`}>
                      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-zinc-300">
                        <div className="w-5 h-5 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                          <CubeIcon className="w-3.5 h-3.5" />
                        </div>
                        <span>Caso de Estudio en Comercio Exterior</span>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-800 dark:text-zinc-200 leading-relaxed">
                        {reglaGeneralSeleccionada.ejemplo}
                      </p>
                    </div>

                    <div className="p-4 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-white/5 bg-slate-50/60 dark:bg-white/[0.02] space-y-2">
                      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400">
                        <div className="w-5 h-5 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                          <TruckIcon className="w-3.5 h-3.5" />
                        </div>
                        <span>Impacto en Almacén WMS & Racks</span>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-700 dark:text-zinc-300 leading-relaxed">
                        {reglaGeneralSeleccionada.aplicacionWMS}
                      </p>
                    </div>
                  </div>

                  {/* Bento Card 4: Fundamento Legal Oficial (DOF - Art. 2 LIGIE) */}
                  <div className="p-4 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-white/5 bg-slate-900 text-slate-200 shadow-inner space-y-2">
                    <div className="flex items-center justify-between text-[11px] font-bold text-slate-400 uppercase tracking-wider pb-1 border-b border-white/10">
                      <span className="flex items-center gap-1.5">
                        <DocumentTextIcon className="w-3.5 h-3.5 text-amber-400" />
                        <span>Texto Oficial DOF (Art. 2 LIGIE)</span>
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono">DOF / OMA</span>
                    </div>
                    <p className="text-xs font-mono leading-relaxed text-slate-300 whitespace-pre-line">
                      {reglaGeneralSeleccionada.textoOficial}
                    </p>
                  </div>
                </div>
                ) : (
                // FICHA TÉCNICA DE REGLA COMPLEMENTARIA MEXICANA
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-4 pb-4 border-b border-slate-200/80 dark:border-white/5">
                        <div className="flex items-center gap-3.5 min-w-0">
                          <IconoIOS
                            icono={reglaComplementariaSeleccionada.icono}
                            gradiente={reglaComplementariaSeleccionada.gradienteIOS}
                            shadow={reglaComplementariaSeleccionada.shadowIOS}
                            tamano="lg"
                          />
                          <div className="space-y-1 min-w-0">
                            <div className="flex items-center gap-2.5 flex-wrap">
                              <span className={`px-2.5 py-0.5 rounded-lg ${reglaComplementariaSeleccionada.colorBadge} font-black text-xs tracking-wider uppercase shadow-xs`}>
                                Regla {reglaComplementariaSeleccionada.numero}
                              </span>
                              <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${reglaComplementariaSeleccionada.colorTag}`}>
                                {reglaComplementariaSeleccionada.categoria}
                              </span>
                            </div>
                            <h2 className="text-base sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight leading-snug">
                              {reglaComplementariaSeleccionada.titulo}
                            </h2>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => copiarTexto(reglaComplementariaSeleccionada.id, `Regla Complementaria ${reglaComplementariaSeleccionada.numero} LIGIE: ${reglaComplementariaSeleccionada.titulo}\n\nExplicación:\n${reglaComplementariaSeleccionada.resumenSimple}\n\nAplicación Aduanera:\n${reglaComplementariaSeleccionada.aplicacion}\n\nTexto DOF:\n${reglaComplementariaSeleccionada.textoOficial}`)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-slate-700 dark:text-zinc-300 text-xs font-bold hover:bg-slate-100 dark:hover:bg-white/10 transition-colors shrink-0 cursor-pointer"
                          title="Copiar regla complementaria"
                        >
                          {copiadoId === reglaComplementariaSeleccionada.id ? (
                            <>
                              <CheckIcon className="w-4 h-4 text-emerald-500" />
                              <span className="text-emerald-600 dark:text-emerald-400">¡Copiado!</span>
                            </>
                          ) : (
                            <>
                              <ClipboardDocumentCheckIcon className="w-4 h-4" />
                              <span className="hidden sm:inline">Copiar Ficha</span>
                            </>
                          )}
                        </button>
                      </div>

                      {/* Explicación y Concepto */}
                      <div className="p-4 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-white/5 bg-slate-50/60 dark:bg-white/[0.02] space-y-2">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400">
                          <div className="w-5 h-5 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                            <LightBulbIcon className="w-3.5 h-3.5" />
                          </div>
                          <span>Clave de Aplicación en México</span>
                        </div>
                        <p className="text-sm sm:text-base text-slate-800 dark:text-zinc-200 leading-relaxed font-medium">
                          {reglaComplementariaSeleccionada.resumenSimple}
                        </p>
                      </div>

                      {/* Aplicación en Aduanas / WMS */}
                      <div className="p-4 sm:p-5 rounded-2xl sm:rounded-3xl border border-blue-500/20 bg-blue-500/5 dark:bg-blue-500/[0.02] space-y-2">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                          <div className="w-5 h-5 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                            <TruckIcon className="w-3.5 h-3.5" />
                          </div>
                          <span>Operatividad en Puerto, Aduana y WMS</span>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-800 dark:text-zinc-200 leading-relaxed">
                          {reglaComplementariaSeleccionada.aplicacion}
                        </p>
                      </div>

                      {/* Texto Legal Oficial */}
                      <div className="p-4 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-white/5 bg-slate-900 text-slate-200 shadow-inner space-y-2">
                        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider pb-1 border-b border-white/10">
                          Texto Legal Oficial (DOF - Art. 2 LIGIE):
                        </div>
                        <p className="text-xs font-mono leading-relaxed text-slate-300 whitespace-pre-line">
                          {reglaComplementariaSeleccionada.textoOficial}
                        </p>
                      </div>
                    </div>
                )}
                  </div>
                </div>
          )}

                {/* VISTA 3: EN LA PRÁCTICA (MATRIZ WMS & PUERTO 4.0) */}
                {tabActiva === 'practica' && (
                  <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6 bg-slate-50/30 dark:bg-[#0f0f10]">
                    <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-500/10 via-emerald-500/10 to-amber-500/10 border border-slate-200/80 dark:border-white/10 text-xs text-slate-700 dark:text-zinc-300 leading-relaxed flex items-center gap-3">
                      <IconoIOS
                        icono={AcademicCapIcon}
                        gradiente="bg-gradient-to-br from-indigo-500 to-purple-600"
                        shadow="shadow-indigo-500/25"
                        tamano="sm"
                      />
                      <div>
                        <strong className="text-slate-900 dark:text-white block font-bold mb-0.5">
                          Matriz de Integración Operativa en LogixPort
                        </strong>
                        Cómo el Artículo 2 de la LIGIE rige cada movimiento físico en los racks del almacén, el pesaje en báscula de SSA Manzanillo y la auditoría de expedientes pedimentarios.
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      {/* Estación 1: WMS */}
                      <div className="p-5 rounded-3xl border border-sky-500/20 bg-white dark:bg-white/[0.02] shadow-sm space-y-3">
                        <div className="flex items-center gap-3 pb-3 border-b border-slate-100 dark:border-white/5">
                          <IconoIOS
                            icono={CubeIcon}
                            gradiente="bg-gradient-to-br from-sky-400 to-blue-600"
                            shadow="shadow-sky-500/25"
                            tamano="sm"
                          />
                          <div>
                            <h4 className="font-extrabold text-sm text-slate-900 dark:text-white">Almacén Práctico (WMS)</h4>
                            <span className="text-[10px] text-slate-400">Racks & Matriz de Huecos</span>
                          </div>
                        </div>
                        <ul className="text-xs text-slate-600 dark:text-zinc-300 space-y-2.5 leading-relaxed">
                          <li className="p-3 rounded-2xl bg-slate-50 dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                            <strong className="text-sky-600 dark:text-sky-400 block mb-1">Regla 2a · Kits Desmontados:</strong>
                            Si una máquina industrial desarmada ocupa 3 pallets en racks distintos, cada tarima mantiene el SKU del producto completo terminado.
                          </li>
                          <li className="p-3 rounded-2xl bg-slate-50 dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                            <strong className="text-sky-600 dark:text-sky-400 block mb-1">Regla 5b · Tara de Pallets:</strong>
                            Las tarimas de madera retornables y racks son tara de transporte; no se acumulan al valor unitario en Kardex.
                          </li>
                          <li className="p-3 rounded-2xl bg-slate-50 dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                            <strong className="text-sky-600 dark:text-sky-400 block mb-1">Regla 3b · Packs Combo:</strong>
                            Los sets para venta al consumidor reciben un único SKU maestro según su componente principal.
                          </li>
                        </ul>
                      </div>

                      {/* Estación 2: Puerto */}
                      <div className="p-5 rounded-3xl border border-cyan-500/20 bg-white dark:bg-white/[0.02] shadow-sm space-y-3">
                        <div className="flex items-center gap-3 pb-3 border-b border-slate-100 dark:border-white/5">
                          <IconoIOS
                            icono={TruckIcon}
                            gradiente="bg-gradient-to-br from-cyan-400 to-teal-600"
                            shadow="shadow-cyan-500/25"
                            tamano="sm"
                          />
                          <div>
                            <h4 className="font-extrabold text-sm text-slate-900 dark:text-white">Despacho Portuario</h4>
                            <span className="text-[10px] text-slate-400">SSA Manzanillo & ANAM</span>
                          </div>
                        </div>
                        <ul className="text-xs text-slate-600 dark:text-zinc-300 space-y-2.5 leading-relaxed">
                          <li className="p-3 rounded-2xl bg-slate-50 dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                            <strong className="text-cyan-600 dark:text-cyan-400 block mb-1">RC 6a · Pesaje VGM:</strong>
                            En báscula portuaria se descuenta la tara del contenedor para verificar estrictamente el peso neto de la carga.
                          </li>
                          <li className="p-3 rounded-2xl bg-slate-50 dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                            <strong className="text-cyan-600 dark:text-cyan-400 block mb-1">RC 8a · Regla 8va:</strong>
                            Despacho coordinado de líneas completas de producción que ingresan divididas en decenas de contenedores marítimos.
                          </li>
                          <li className="p-3 rounded-2xl bg-slate-50 dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                            <strong className="text-cyan-600 dark:text-cyan-400 block mb-1">RC 10a · Plazo 15 Días:</strong>
                            Plazo legal improrrogable para ingresar dictámenes de clasificación si surge controversia en semáforo fiscal.
                          </li>
                        </ul>
                      </div>

                      {/* Estación 3: Inteligencia */}
                      <div className="p-5 rounded-3xl border border-indigo-500/20 bg-white dark:bg-white/[0.02] shadow-sm space-y-3">
                        <div className="flex items-center gap-3 pb-3 border-b border-slate-100 dark:border-white/5">
                          <IconoIOS
                            icono={ScaleIcon}
                            gradiente="bg-gradient-to-br from-indigo-500 to-purple-600"
                            shadow="shadow-indigo-500/25"
                            tamano="sm"
                          />
                          <div>
                            <h4 className="font-extrabold text-sm text-slate-900 dark:text-white">Inteligencia Aduanera</h4>
                            <span className="text-[10px] text-slate-400">Auditoría LIGIE & NICO</span>
                          </div>
                        </div>
                        <ul className="text-xs text-slate-600 dark:text-zinc-300 space-y-2.5 leading-relaxed">
                          <li className="p-3 rounded-2xl bg-slate-50 dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                            <strong className="text-indigo-600 dark:text-indigo-400 block mb-1">Jerarquía Estricta:</strong>
                            Nunca acudas a la Regla 3 si el texto de la partida o las notas legales bajo la Regla 1 ya resuelven la clasificación.
                          </li>
                          <li className="p-3 rounded-2xl bg-slate-50 dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                            <strong className="text-indigo-600 dark:text-indigo-400 block mb-1">RC 3a · Notas DOF:</strong>
                            Cita obligatoria de las Notas Explicativas del DOF para sustentar peritajes y justificaciones arancelarias.
                          </li>
                          <li className="p-3 rounded-2xl bg-slate-50 dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                            <strong className="text-indigo-600 dark:text-indigo-400 block mb-1">RC 2a · Código 99:</strong>
                            Identificación expedita de fracciones genéricas terminales con terminación 99 para mercancías sin partida específica.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* FOOTER DEL MODAL (Limpio e institucional) */}
              <div className="px-5 sm:px-8 py-3.5 border-t border-slate-100 dark:border-white/5 flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400 shrink-0 bg-white/95 dark:bg-[#0f0f10]/95 backdrop-blur-xl">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block animate-pulse" />
                  <span className="truncate">
                    Fundamento Legal: Ley de los Impuestos Generales de Importación y de Exportación (LIGIE), Artículo 2.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={onCerrar}
                  className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-zinc-200 text-white dark:text-black font-bold transition-colors cursor-pointer text-xs shrink-0"
                >
                  Cerrar Ficha
                </button>
              </div>
            </div>
    </div>
        );
};

        export interface BotonReglasLigieProps {
          onClick: () => void;
        className?: string;
        variante?: 'default' | 'compacto' | 'outline' | 'amber';
        texto?: string;
}

        export const BotonReglasLigie: React.FC<BotonReglasLigieProps> = ({
          onClick,
          className = '',
          variante = 'default',
          texto = 'Reglas LIGIE (Art. 2)'
        }) => {
  if (variante === 'amber') {
    return (
          <button
            type="button"
            onClick={onClick}
            title="Consultar Artículo 2 LIGIE: Las 6 Reglas Generales y 10 Complementarias"
            className={`flex-1 sm:flex-none justify-center group relative flex items-center gap-2 overflow-hidden rounded-xl border border-amber-500/30 bg-amber-500/10 px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-bold text-amber-700 dark:text-amber-300 shadow-xs backdrop-blur-sm transition-all hover:bg-amber-500/20 hover:border-amber-500/50 cursor-pointer ${className}`}
          >
            <ScaleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 transition-transform group-hover:scale-110 shrink-0" />
            <span className="whitespace-nowrap">{texto}</span>
          </button>
          );
  }

          if (variante === 'compacto') {
    return (
          <button
            type="button"
            onClick={onClick}
            title="Consultar Artículo 2 LIGIE: Las 6 Reglas Generales y 10 Complementarias"
            className={`p-2 rounded-xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-300 transition-colors cursor-pointer ${className}`}
          >
            <ScaleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </button>
          );
  }

          if (variante === 'outline') {
    return (
          <button
            type="button"
            onClick={onClick}
            title="Consultar Artículo 2 LIGIE: Las 6 Reglas Generales y 10 Complementarias"
            className={`flex-1 sm:flex-none justify-center group relative flex items-center gap-2 overflow-hidden rounded-xl border border-slate-200/60 bg-white/60 dark:border-white/10 dark:bg-white/[0.03] px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-bold text-slate-700 dark:text-zinc-300 shadow-xs backdrop-blur-sm transition-all hover:border-slate-300 hover:bg-white dark:hover:bg-white/[0.07] cursor-pointer ${className}`}
          >
            <ScaleIcon className="h-4 w-4 text-indigo-500 dark:text-indigo-400 transition-transform group-hover:scale-110 shrink-0" />
            <span className="whitespace-nowrap">{texto}</span>
          </button>
          );
  }

          return (
          <button
            type="button"
            onClick={onClick}
            title="Consultar Artículo 2 LIGIE: Las 6 Reglas Generales y 10 Complementarias"
            className={`flex-1 sm:flex-none justify-center group relative flex items-center gap-2 overflow-hidden rounded-xl border border-blue-500/30 bg-blue-500/10 px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-bold text-blue-700 dark:text-blue-300 shadow-xs backdrop-blur-sm transition-all hover:bg-blue-500/20 hover:border-blue-500/50 cursor-pointer ${className}`}
          >
            <ScaleIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 transition-transform group-hover:scale-110 shrink-0" />
            <span className="whitespace-nowrap">{texto}</span>
          </button>
          );
};

          export default ModalReglasLigie;
